import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { IconBadgeComponent } from '@app/shared/components/icon-badge/icon-badge.component';
import { LdEventStatus } from '@app/main/leak-detection/enums/ld-event-status.enum';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import { LdEventRepairStatus } from '@app/main/leak-detection/enums/ld-event-repair-status.enum';
import { ldEventStatusLocalize } from '@app/main/leak-detection/localize/ld-event-status';
import {
  HIDDEN_EVENT_BADGE_BACKGROUND,
  ldEventInvestigationResultIcon,
  ldEventStatusIcon,
} from '@app/main/leak-detection/configs/ld-event-status.config';
import { getLdEventStatusBackground } from '@app/main/leak-detection/utils/ld-event-status-icon';
import {
  DIALOG_EVENT_HIDDEN_BADGE_THEME,
  DIALOG_EVENT_NEUTRAL_BADGE_THEME,
  dialogEventRepairOverlayIcon,
  dialogEventResultBadgeTheme,
  dialogEventResultLabel,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-badge.config';

@Component({
  selector: 'gc-dialog-event-badges',
  standalone: true,
  imports: [TranslateDirective, TranslatePipe, IconBadgeComponent],
  templateUrl: './dialog-event-badges.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex items-center gap-[0.5rem] flex-wrap' },
})
export class DialogEventBadgesComponent {
  public status = input.required<LdEventStatus>();
  public investigationResult = input<LdEventInvestigationResult | null>(null);
  public repairStatus = input<LdEventRepairStatus | null>(null);
  /** greys the badges out, as a hidden event is on the map and in the Events table */
  public hiddenEvent = input(false);

  protected readonly statusLabel = computed(() => ldEventStatusLocalize[this.status()]);
  protected readonly statusIcon = computed(() => ldEventStatusIcon[this.status()]);
  protected readonly statusIconBackground = computed(() =>
    this.hiddenEvent() ? HIDDEN_EVENT_BADGE_BACKGROUND : getLdEventStatusBackground(this.status())
  );

  protected readonly statusTheme = computed(() =>
    this.hiddenEvent() ? DIALOG_EVENT_HIDDEN_BADGE_THEME : DIALOG_EVENT_NEUTRAL_BADGE_THEME
  );

  protected readonly resultIcon = computed(() => {
    const result = this.investigationResult();

    return result ? ldEventInvestigationResultIcon[result] : null;
  });

  /** undefined keeps the badge's own default, as the Events table does for a visible event */
  protected readonly resultIconBackground = computed(() =>
    this.hiddenEvent() ? HIDDEN_EVENT_BADGE_BACKGROUND : undefined
  );

  protected readonly resultOverlayIcon = computed(() => {
    const repairStatus = this.repairStatus();

    return repairStatus ? dialogEventRepairOverlayIcon[repairStatus] : undefined;
  });

  protected readonly resultLabel = computed(() => {
    const result = this.investigationResult();

    return result ? dialogEventResultLabel(result, this.repairStatus()) : null;
  });

  protected readonly resultTheme = computed(() => {
    const repairStatus = this.repairStatus();

    if (this.hiddenEvent()) return DIALOG_EVENT_HIDDEN_BADGE_THEME;

    return repairStatus ? dialogEventResultBadgeTheme[repairStatus] : DIALOG_EVENT_NEUTRAL_BADGE_THEME;
  });
}
